// src/app/time-attendance/components/payable-hours-report/payable-hours-report.component.ts

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { PayableHoursReportService } from './services/payable-hours-report.service';
import {
  EmployeeSearchDto,
  PayableHoursReportQuery,
  PayableHoursReportRowDto,
  PayableHoursReportTotalsVm,
  UserFilterValuesDto,
  WorkOrderNumberSearchDto,
  YywwDto,
} from './models/payable-hours-report.model';

type ToastKind = 'success' | 'error' | 'info';
type FilterDropdown = 'companies' | 'departments' | 'payGroups';

interface SelectedFilterState {
  yywwId: number | null;
  companyIds: number[];
  departmentIds: number[];
  payGroupIds: number[];
  employeeIds: number[];
  workOrderNumberIds: number[];
}

@Component({
  selector: 'app-payable-hours-report',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, DecimalPipe],
  templateUrl: './payable-hours-report.component.html',
  styleUrls: ['./payable-hours-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayableHoursReportComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly employeeSearch$ = new Subject<string>();
  private readonly workOrderSearch$ = new Subject<string>();

  isLoading = true;
  isReportLoading = false;
  isEmployeeSearchLoading = false;
  isWorkOrderSearchLoading = false;

  filtersModalOpen = false;
  openDropdown: FilterDropdown | null = null;

  errorMessage: string | null = null;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  filterValues: UserFilterValuesDto | null = null;
  weeks: YywwDto[] = [];

  rows: PayableHoursReportRowDto[] = [];
  employeeResults: EmployeeSearchDto[] = [];
  workOrderResults: WorkOrderNumberSearchDto[] = [];

  employeeSearchTerm = '';
  workOrderSearchTerm = '';

  selectedEmployees: EmployeeSearchDto[] = [];
  selectedWorkOrders: WorkOrderNumberSearchDto[] = [];

  filters: SelectedFilterState = {
    yywwId: null,
    companyIds: [],
    departmentIds: [],
    payGroupIds: [],
    employeeIds: [],
    workOrderNumberIds: [],
  };

  totals: PayableHoursReportTotalsVm = {
    totalHours: 0,
    regularHours: 0,
    overtimeHours: 0,
    rowCount: 0,
  };

  constructor(
    private readonly payableHoursReportSvc: PayableHoursReportService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.setupEmployeeSearch();
    this.setupWorkOrderSearch();

    void this.loadInitialData();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('modal-open');

    this.destroy$.next();
    this.destroy$.complete();
    this.clearToast();
  }

  async loadInitialData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.detect();

    try {
      const currentYear = new Date().getFullYear();

      const [filterValues, weeks] = await Promise.all([
        firstValueFrom(this.payableHoursReportSvc.getFilterValues()),
        firstValueFrom(this.payableHoursReportSvc.getAllYywwsByYear(currentYear)),
      ]);

      this.filterValues = filterValues;
      this.weeks = weeks;

      const currentWeek = this.getCurrentWeek(weeks);
      this.filters.yywwId = currentWeek?.id ?? weeks.at(-1)?.id ?? null;

      await this.loadReport();
    } catch (error) {
      console.error('loadInitialData error', error);
      this.errorMessage = 'Failed to load payable hours report filters.';
      this.showToast('error', 'Failed to load payable hours report filters.');
    } finally {
      this.isLoading = false;
      this.detect();
    }
  }

  async loadReport(): Promise<void> {
    if (!this.filters.yywwId) {
      this.rows = [];
      this.resetTotals();
      this.errorMessage = 'Please select a week before loading the report.';
      this.showToast('info', 'Please select a week.');
      this.detect();
      return;
    }

    this.isReportLoading = true;
    this.errorMessage = null;
    this.detect();

    try {
      const query: PayableHoursReportQuery = {
        yywwId: this.filters.yywwId,
        companyIds: this.filters.companyIds,
        departmentIds: this.filters.departmentIds,
        payGroupIds: this.filters.payGroupIds,
        employeeIds: this.filters.employeeIds,
        workOrderNumberIds: this.filters.workOrderNumberIds,
      };

      this.rows = await firstValueFrom(this.payableHoursReportSvc.getReport(query));
      this.updateTotals();
    } catch (error) {
      console.error('get payable hours report error', error);
      this.rows = [];
      this.resetTotals();
      this.errorMessage = 'Failed to load payable hours report.';
      this.showToast('error', 'Failed to load payable hours report.');
    } finally {
      this.isReportLoading = false;
      this.detect();
    }
  }

  refreshReport(): void {
    void this.loadReport();
  }

  applyFilters(): void {
    void this.loadReport();
  }

  openFiltersModal(): void {
    this.filtersModalOpen = true;
    document.body.classList.add('modal-open');
    this.detect();
  }

  closeFiltersModal(): void {
    this.filtersModalOpen = false;
    this.openDropdown = null;
    document.body.classList.remove('modal-open');
    this.detect();
  }

  applyFiltersAndClose(): void {
    if (!this.filters.yywwId) {
      this.showToast('info', 'Please select a week.');
      return;
    }

    this.closeFiltersModal();
    void this.loadReport();
  }

  resetFilters(): void {
    const currentWeek = this.getCurrentWeek(this.weeks);

    this.filters = {
      yywwId: currentWeek?.id ?? this.weeks.at(-1)?.id ?? null,
      companyIds: [],
      departmentIds: [],
      payGroupIds: [],
      employeeIds: [],
      workOrderNumberIds: [],
    };

    this.employeeSearchTerm = '';
    this.workOrderSearchTerm = '';
    this.employeeResults = [];
    this.workOrderResults = [];
    this.selectedEmployees = [];
    this.selectedWorkOrders = [];
    this.openDropdown = null;

    void this.loadReport();
  }

  onYywwChange(raw: string): void {
    const yywwId = Number(raw);
    this.filters.yywwId = Number.isFinite(yywwId) && yywwId > 0 ? yywwId : null;
    this.detect();
  }

  toggleDropdown(name: FilterDropdown): void {
    this.openDropdown = this.openDropdown === name ? null : name;
    this.detect();
  }

  closeDropdown(): void {
    this.openDropdown = null;
    this.detect();
  }

  toggleCompanyId(companyId: number): void {
    this.filters.companyIds = this.toggleNumber(this.filters.companyIds, companyId);
    this.detect();
  }

  toggleDepartmentId(departmentId: number): void {
    this.filters.departmentIds = this.toggleNumber(this.filters.departmentIds, departmentId);
    this.detect();
  }

  togglePayGroupId(payGroupId: number): void {
    this.filters.payGroupIds = this.toggleNumber(this.filters.payGroupIds, payGroupId);
    this.detect();
  }

  isCompanySelected(companyId: number): boolean {
    return this.filters.companyIds.includes(companyId);
  }

  isDepartmentSelected(departmentId: number): boolean {
    return this.filters.departmentIds.includes(departmentId);
  }

  isPayGroupSelected(payGroupId: number): boolean {
    return this.filters.payGroupIds.includes(payGroupId);
  }

  getCompanySelectionLabel(): string {
    if (!this.filters.companyIds.length) {
      return 'All companies';
    }

    const names = (this.filterValues?.companies ?? [])
      .filter((company) => this.filters.companyIds.includes(company.id))
      .map((company) => company.name);

    if (!names.length) {
      return 'All companies';
    }

    return names.length <= 2 ? names.join(', ') : `${names.length} companies selected`;
  }

  getDepartmentSelectionLabel(): string {
    if (!this.filters.departmentIds.length) {
      return 'All departments';
    }

    const names = (this.filterValues?.departments ?? [])
      .filter((department) => this.filters.departmentIds.includes(department.id))
      .map((department) => `${department.code} - ${department.name}`);

    if (!names.length) {
      return 'All departments';
    }

    return names.length <= 2 ? names.join(', ') : `${names.length} departments selected`;
  }

  getPayGroupSelectionLabel(): string {
    if (!this.filters.payGroupIds.length) {
      return 'All pay groups';
    }

    const names = (this.filterValues?.payGroups ?? [])
      .filter((payGroup) => this.filters.payGroupIds.includes(payGroup.id))
      .map((payGroup) => payGroup.name);

    if (!names.length) {
      return 'All pay groups';
    }

    return names.length <= 2 ? names.join(', ') : `${names.length} pay groups selected`;
  }

  onEmployeeSearchInput(event: Event): void {
    const value = this.getInputValue(event);
    this.employeeSearchTerm = value;
    this.employeeSearch$.next(value);
    this.detect();
  }

  onWorkOrderSearchInput(event: Event): void {
    const value = this.getInputValue(event);
    this.workOrderSearchTerm = value;
    this.workOrderSearch$.next(value);
    this.detect();
  }

  addEmployee(employee: EmployeeSearchDto): void {
    if (this.filters.employeeIds.includes(employee.id)) {
      return;
    }

    this.selectedEmployees = [...this.selectedEmployees, employee];
    this.filters.employeeIds = [...this.filters.employeeIds, employee.id];
    this.employeeResults = this.employeeResults.filter((row) => row.id !== employee.id);
    this.employeeSearchTerm = '';
    this.detect();
  }

  removeEmployee(employeeId: number): void {
    this.selectedEmployees = this.selectedEmployees.filter((employee) => employee.id !== employeeId);
    this.filters.employeeIds = this.filters.employeeIds.filter((id) => id !== employeeId);
    this.detect();
  }

  addWorkOrderNumber(workOrder: WorkOrderNumberSearchDto): void {
    if (this.filters.workOrderNumberIds.includes(workOrder.id)) {
      return;
    }

    this.selectedWorkOrders = [...this.selectedWorkOrders, workOrder];
    this.filters.workOrderNumberIds = [...this.filters.workOrderNumberIds, workOrder.id];
    this.workOrderResults = this.workOrderResults.filter((row) => row.id !== workOrder.id);
    this.workOrderSearchTerm = '';
    this.detect();
  }

  removeWorkOrderNumber(workOrderNumberId: number): void {
    this.selectedWorkOrders = this.selectedWorkOrders.filter((won) => won.id !== workOrderNumberId);
    this.filters.workOrderNumberIds = this.filters.workOrderNumberIds.filter(
      (id) => id !== workOrderNumberId,
    );
    this.detect();
  }

  exportToExcel(): void {
    if (!this.rows.length) {
      this.showToast('info', 'No rows to export.');
      return;
    }

    const headers = [
      'Employee',
      'Employee Number',
      'RFID',
      'Company',
      'Department',
      'Department Code',
      'Apply Date',
      'Hours',
      'Pay Code',
      'Pay Code Type',
      'Pay Group',
      'YYWW ID',
      'Synced',
      'Sync ID',
      'Synced At',
      'Synced By',
      'Booked Hour ID',
      'Booked WON',
    ];

    const csvRows = this.rows.map((row) => {
      return [
        this.getEmployeeName(row),
        row.employee.employeeNumber ?? '',
        row.employee.rfid ?? '',
        row.employee.company?.name ?? '',
        row.employee.department?.name ?? '',
        row.employee.department?.code ?? '',
        row.applyDate,
        this.formatNumber(row.hours),
        row.payCode?.name ?? '',
        row.payCode?.type ?? '',
        row.payGroup?.name ?? '',
        row.yywwId,
        row.payableHoursSync ? 'Yes' : 'No',
        row.payableHoursSync?.id ?? '',
        row.payableHoursSync?.createdAt ?? '',
        this.getSyncedBy(row),
        row.bookedHour?.id ?? '',
        row.bookedHour?.workOrderNumber?.won ?? '',
      ];
    });

    const csv = [headers, ...csvRows]
      .map((values) =>
        values
          .map((value) => {
            const text = String(value ?? '');
            return `"${text.replace(/"/g, '""')}"`;
          })
          .join(','),
      )
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const weekLabel = this.getSelectedWeekLabel().replace(/[^a-zA-Z0-9_-]/g, '');
    anchor.href = url;
    anchor.download = `payable-hours-report-${weekLabel || 'export'}.csv`;
    anchor.click();

    URL.revokeObjectURL(url);
    this.showToast('success', 'Report exported.');
  }

  getEmployeeName(rowOrEmployee: PayableHoursReportRowDto | EmployeeSearchDto): string {
    const employee = 'employee' in rowOrEmployee ? rowOrEmployee.employee : rowOrEmployee;
    return `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || '—';
  }

  getEmployeeSubLabel(employee: EmployeeSearchDto): string {
    const parts = [
      employee.employeeNumber ? `#${employee.employeeNumber}` : null,
      employee.company?.name ?? null,
      employee.department ? `${employee.department.code} - ${employee.department.name}` : null,
    ].filter(Boolean);

    return parts.join(' • ') || '—';
  }

  getWorkOrderSubLabel(workOrder: WorkOrderNumberSearchDto): string {
    const parts = [
      workOrder.description ?? null,
      workOrder.project?.name ? `Project ${workOrder.project.name}` : null,
      workOrder.department ? `${workOrder.department.code} - ${workOrder.department.name}` : null,
    ].filter(Boolean);

    return parts.join(' • ') || '—';
  }

  getDepartmentDisplay(row: PayableHoursReportRowDto): string {
    const department = row.employee.department;
    return department ? `${department.code} - ${department.name}` : '—';
  }

  getCompanyDisplay(row: PayableHoursReportRowDto): string {
    return row.employee.company?.name ?? '—';
  }

  getSyncedBy(row: PayableHoursReportRowDto): string {
    const user = row.payableHoursSync?.createdBy;
    return user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '—' : '—';
  }

  getSyncStatus(row: PayableHoursReportRowDto): string {
    return row.payableHoursSync ? 'Synced' : 'Not Synced';
  }

  getSelectedWeekLabel(): string {
    const selectedWeek = this.weeks.find((week) => week.id === this.filters.yywwId);
    return selectedWeek?.fullYearWeek ?? 'No Week Selected';
  }

  getSelectedWeekDateRange(): string {
    const selectedWeek = this.weeks.find((week) => week.id === this.filters.yywwId);
    return selectedWeek ? `${selectedWeek.startDate} - ${selectedWeek.endDate}` : '';
  }

  getActiveFilterCount(): number {
    return (
      (this.filters.yywwId ? 1 : 0) +
      this.filters.companyIds.length +
      this.filters.departmentIds.length +
      this.filters.payGroupIds.length +
      this.filters.employeeIds.length +
      this.filters.workOrderNumberIds.length
    );
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = null;
    this.toastMessage = null;
    this.detect();
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }

  private setupEmployeeSearch(): void {
    this.employeeSearch$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term) => {
          const cleanTerm = term.trim();

          if (!cleanTerm) {
            this.isEmployeeSearchLoading = false;
            this.employeeResults = [];
            this.detect();
            return of([]);
          }

          this.isEmployeeSearchLoading = true;
          this.detect();

          return this.payableHoursReportSvc
            .searchEmployees({
              searchTerm: cleanTerm,
              pageSize: 100,
            })
            .pipe(
              catchError((error) => {
                console.error('searchEmployees error', error);
                this.showToast('error', 'Failed to search employees.');
                return of([]);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((results) => {
        this.employeeResults = results.filter((employee) => {
          return !this.filters.employeeIds.includes(employee.id);
        });

        this.isEmployeeSearchLoading = false;
        this.detect();
      });
  }

  private setupWorkOrderSearch(): void {
    this.workOrderSearch$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term) => {
          const cleanTerm = term.trim();

          if (!cleanTerm) {
            this.isWorkOrderSearchLoading = false;
            this.workOrderResults = [];
            this.detect();
            return of([]);
          }

          this.isWorkOrderSearchLoading = true;
          this.detect();

          return this.payableHoursReportSvc
            .searchWorkOrderNumbers({
              searchTerm: cleanTerm,
              pageSize: 20,
            })
            .pipe(
              catchError((error) => {
                console.error('searchWorkOrderNumbers error', error);
                this.showToast('error', 'Failed to search work order numbers.');
                return of([]);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((results) => {
        this.workOrderResults = results.filter((won) => {
          return !this.filters.workOrderNumberIds.includes(won.id);
        });

        this.isWorkOrderSearchLoading = false;
        this.detect();
      });
  }

  private toggleNumber(values: number[], id: number): number[] {
    return values.includes(id) ? values.filter((value) => value !== id) : [...values, id];
  }

  private getInputValue(event: Event): string {
    return (event.target as HTMLInputElement | null)?.value ?? '';
  }

  private updateTotals(): void {
    const totalHours = this.rows.reduce((sum, row) => sum + this.toNumber(row.hours), 0);

    const regularHours = this.rows
      .filter((row) => String(row.payCode?.type ?? '').toLowerCase() !== 'ot')
      .reduce((sum, row) => sum + this.toNumber(row.hours), 0);

    const overtimeHours = this.rows
      .filter((row) => String(row.payCode?.type ?? '').toLowerCase() === 'ot')
      .reduce((sum, row) => sum + this.toNumber(row.hours), 0);

    this.totals = {
      totalHours: this.round2(totalHours),
      regularHours: this.round2(regularHours),
      overtimeHours: this.round2(overtimeHours),
      rowCount: this.rows.length,
    };
  }

  private resetTotals(): void {
    this.totals = {
      totalHours: 0,
      regularHours: 0,
      overtimeHours: 0,
      rowCount: 0,
    };
  }

  private getCurrentWeek(weeks: YywwDto[]): YywwDto | null {
    const today = this.toDateOnly(new Date());

    return (
      weeks.find((week) => {
        return week.startDate <= today && week.endDate >= today;
      }) ?? null
    );
  }

  private toDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private toNumber(value: number | string | null | undefined): number {
    const next = Number(value);
    return Number.isFinite(next) ? next : 0;
  }

  private round2(value: number): number {
    return Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
  }

  private formatNumber(value: number | string | null | undefined): string {
    return this.round2(this.toNumber(value)).toFixed(2);
  }

  private showToast(kind: ToastKind, message: string): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => this.clearToast(), 2600);
    this.detect();
  }

  private detect(): void {
    if (!(this.cdr as unknown as { destroyed?: boolean }).destroyed) {
      this.cdr.detectChanges();
    }
  }
}